<template>
    <div class="bd">
        <div class="my-custom-table">
            <a-page-header title="订单记录" />
            <div class="search-bar">
                <div class="tabInput">
                    <a class="query-label">支付单号：</a>
                    <a-input v-model:value="query.outTradeNo" placeholder="请输入支付单号" class="search-input" allow-clear
                        @press-enter="onSearch" />
                </div>
                <div class="tabInput">
                    <a class="query-label">业务订单号：</a>
                    <a-input v-model:value="query.bizOrderNo" placeholder="请输入业务订单号" class="search-input" allow-clear
                        @press-enter="onSearch" />
                </div>
                <div class="tabInput">
                    <a class="query-label">状态：</a>
                    <a-select v-model:value="query.status" placeholder="请选择" class="search-select" allow-clear
                        :options="PAY_STATUS_OPTIONS" />
                </div>
                <div class="tabInput">
                    <a class="query-label">开始时间：</a>
                    <a-date-picker v-model:value="query.beginTime" show-time valueFormat="YYYY-MM-DD HH:mm:ss"
                        placeholder="请选择开始时间" class="search-input" />
                </div>
                <div class="tabInput">
                    <a class="query-label">结束时间：</a>
                    <a-date-picker v-model:value="query.endTime" show-time valueFormat="YYYY-MM-DD HH:mm:ss"
                        placeholder="请选择结束时间" class="search-input" />
                </div>
                <div class="tab-button">
                    <div>
                        <a-button class="search-tow" type="primary" @click="onSearch"
                            :icon="h(SearchOutlined)">搜索</a-button>
                        <a-button class="search-tow" @click="onReset" :icon="h(RedoOutlined)">重置</a-button>
                    </div>
                </div>
            </div>

            <a-table :columns="columns" :data-source="data" :pagination="pagination" row-key="id"
                :scroll="{ y: 'calc(100vh - 360px)' }" class="flex-table">
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'totalAmount'">
                        <span class="now-price">{{ formatYuan(record.totalAmount) }}</span>
                    </template>
                    <template v-if="column.key === 'payerTotal'">
                        <span v-if="record.payerTotal > 0">{{ formatYuan(record.payerTotal) }}</span>
                        <span v-else>-</span>
                    </template>
                    <template v-if="column.key === 'status'">
                        <a-tag :color="payStatusColor(record.status)">{{ payStatusLabel(record.status) }}</a-tag>
                    </template>
                    <template v-if="column.key === 'successTime'">
                        {{ record.successTime || '-' }}
                    </template>
                    <template v-if="column.dataIndex === 'operation'">
                        <div class="editable-row-operations">
                            <a @click="showDetail(record.id)">详情</a>
                            <!-- 仅待支付可关闭 -->
                            <template v-if="record.status === 'WAIT_PAY'">
                                <a-divider type="vertical" />
                                <a-popconfirm title="后端会先核查微信侧真实状态再关单，关闭支付单不影响业务订单。确认关闭？" ok-text="确认关闭"
                                    cancel-text="取消" @confirm="closeOrder(record.id)">
                                    <a class="danger-link" style="margin: 15px;">关闭</a>
                                </a-popconfirm>
                            </template>
                        </div>
                    </template>
                </template>
            </a-table>

            <a-modal v-model:open="detailOpen" title="支付单详情" :width="780" :footer="null">
                <a-descriptions bordered :column="2" size="middle">
                    <a-descriptions-item label="支付单号">{{ detail.outTradeNo || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="业务订单号">{{ detail.bizOrderNo || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="状态">
                        <a-tag :color="payStatusColor(detail.status)">{{ payStatusLabel(detail.status) }}</a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="支付渠道">{{ detail.channel || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="商品描述" :span="2">{{ detail.description || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="下单金额">
                        <span class="now-price">{{ formatYuan(detail.totalAmount) }}</span>
                    </a-descriptions-item>
                    <a-descriptions-item label="实付金额">
                        {{ detail.payerTotal > 0 ? formatYuan(detail.payerTotal) : '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="支付方式">{{ detail.payWay || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="用户 openid">{{ detail.openid || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="微信支付单号" :span="2">{{ detail.transactionId || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="预支付ID" :span="2">{{ detail.prepayId || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="附加数据" :span="2">{{ detail.attach || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="业务回调地址" :span="2">{{ detail.notifyUrl || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="支付时间">{{ detail.successTime || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="过期时间">{{ detail.expireTime || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="创建时间">{{ detail.createTime || '-' }}</a-descriptions-item>
                    <a-descriptions-item label="更新时间">{{ detail.updateTime || '-' }}</a-descriptions-item>
                </a-descriptions>
            </a-modal>
        </div>
    </div>
</template>

<script>
import { payOrderPage, payOrderInfo, closePayOrder } from '@/api/service';
import { message } from 'ant-design-vue';
import { SearchOutlined, RedoOutlined } from '@ant-design/icons-vue';
import { ref, reactive, h } from 'vue';

const PAY_STATUS_OPTIONS = [
    { value: 'WAIT_PAY', label: '待支付' },
    { value: 'PAID', label: '已支付' },
    { value: 'CLOSED', label: '已关闭' },
];
const PAY_STATUS_MAP = {
    WAIT_PAY: { label: '待支付', color: 'orange' },
    PAID: { label: '已支付', color: 'green' },
    CLOSED: { label: '已关闭', color: 'default' },
};

export default {
    name: 'PayRecordPage',
    setup() {
        const data = ref([]);
        const detail = ref({});
        const detailOpen = ref(false);
        const query = reactive({
            outTradeNo: '',
            bizOrderNo: '',
            status: undefined,
            beginTime: '',
            endTime: '',
        });

        const pagination = ref({
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (page, pageSize) => {
                pagination.value.current = page;
                pagination.value.pageSize = pageSize;
                list();
            },
            position: ['bottomLeft']
        });

        const columns = [
            { title: '支付单号', dataIndex: 'outTradeNo', key: 'outTradeNo', width: '20%' },
            { title: '业务订单号', dataIndex: 'bizOrderNo', key: 'bizOrderNo', width: '20%' },
            { title: '商品描述', dataIndex: 'description', key: 'description', width: '12%' },
            { title: '下单金额', dataIndex: 'totalAmount', key: 'totalAmount', width: '9%' },
            { title: '实付金额', dataIndex: 'payerTotal', key: 'payerTotal', width: '9%' },
            { title: '状态', dataIndex: 'status', key: 'status', width: '8%' },
            { title: '支付时间', dataIndex: 'successTime', key: 'successTime', width: '12%' },
            { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: '12%' },
            { title: '操作', dataIndex: 'operation', key: 'operation', width: '10%' },
        ];

        // 金额：分 → ￥元（空值显示 -）
        const formatYuan = (fen) => (fen === null || fen === undefined ? '-' : '￥' + (Number(fen) / 100).toFixed(2));
        const payStatusLabel = (status) => (PAY_STATUS_MAP[status] ? PAY_STATUS_MAP[status].label : (status || '-'));
        const payStatusColor = (status) => (PAY_STATUS_MAP[status] ? PAY_STATUS_MAP[status].color : 'default');

        const list = async () => {
            const response = await payOrderPage({
                pageNumber: pagination.value.current,
                pageSize: pagination.value.pageSize,
                outTradeNo: query.outTradeNo || undefined,
                bizOrderNo: query.bizOrderNo || undefined,
                status: query.status || undefined,
                beginTime: query.beginTime || undefined,
                endTime: query.endTime || undefined,
            });
            if (response.code === 200) {
                const page = response.data || {};
                pagination.value.total = page.totalRow || page.total || 0;
                data.value = page.records || [];
            } else {
                message.error(response.msg || '查询失败');
            }
        };

        list();

        // 详情：列表接口已返回全字段，仍走详情接口保证数据最新
        const showDetail = async (id) => {
            const response = await payOrderInfo(id);
            if (response.code === 200) {
                detail.value = response.data || {};
                detailOpen.value = true;
            } else {
                message.error(response.msg || '查询详情失败');
            }
        };

        // 关闭：后端委托支付服务核查微信侧状态，成功/失败文案均以后端 msg 为准
        const closeOrder = async (id) => {
            const response = await closePayOrder(id);
            if (response.code === 200) {
                message.success(response.msg || '关闭成功');
            } else {
                // 如"该业务订单已支付成功，不能关闭"、"订单支付处理中，请稍后重试"
                message.error(response.msg || '关闭失败');
            }
            // 失败时微信侧可能已支付，状态已变，统一刷新
            list();
        };

        const onSearch = () => {
            pagination.value.current = 1;
            list();
        };

        const onReset = () => {
            query.outTradeNo = '';
            query.bizOrderNo = '';
            query.status = undefined;
            query.beginTime = '';
            query.endTime = '';
            pagination.value.current = 1;
            list();
        };

        return {
            PAY_STATUS_OPTIONS,
            columns,
            pagination,
            data,
            query,
            detail,
            detailOpen,
            SearchOutlined,
            RedoOutlined,
            formatYuan,
            payStatusLabel,
            payStatusColor,
            showDetail,
            closeOrder,
            onSearch,
            onReset,
            h,
        };
    },
};
</script>

<style scoped>
.search-bar {
    flex-wrap: wrap;
    align-items: center;
}

.tabInput {
    width: auto;
    margin: 0 12px 10px 0;
}

.query-label {
    white-space: nowrap;
}

.search-select {
    width: 140px;
}

.tab-button {
    width: auto;
    flex: 1;
    margin-bottom: 10px;
}

.now-price {
    color: #e74c3c;
    font-weight: bold;
}

.danger-link {
    color: #ff4d4f;
}
</style>
